import { useState } from "react";
import axios from "axios";
import { COMPANY, PRODUCTS } from "@/data";
import { Mail, MapPin, Phone, CheckCircle2, AlertCircle, Loader2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PROJECT_TYPES = [
  "New home / custom residential",
  "Multi-unit / townhouse",
  "Cottage / camp",
  "Agricultural building",
  "Commercial / industrial",
  "Addition / renovation",
  "Other",
];

const TIMELINES = [
  "ASAP (< 2 weeks)",
  "2–4 weeks",
  "1–3 months",
  "3+ months",
  "Planning / no firm date",
];

export default function Quote() {
  const [form, setForm] = useState({
    name: "",
    company: "",
    email: "",
    phone: "",
    project_location: "",
    project_type: "",
    products: [],
    timeline: "",
    details: "",
  });
  const [status, setStatus] = useState({ type: null, msg: "" });
  const [submitting, setSubmitting] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const toggleProduct = (title) => {
    setForm((f) => ({
      ...f,
      products: f.products.includes(title)
        ? f.products.filter((p) => p !== title)
        : [...f.products, title],
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setStatus({ type: null, msg: "" });
    if (!form.name || !form.email || !form.details) {
      setStatus({ type: "error", msg: "Please fill in name, email, and project details." });
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/quote-request`, form);
      setStatus({ type: "success", msg: res.data.message });
      setForm({
        name: "",
        company: "",
        email: "",
        phone: "",
        project_location: "",
        project_type: "",
        products: [],
        timeline: "",
        details: "",
      });
    } catch (err) {
      setStatus({
        type: "error",
        msg:
          err?.response?.data?.detail ||
          "Something went wrong. Please email " + COMPANY.email + " directly.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="quote-page" className="pt-32 pb-20">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-12 gap-0 border border-stone-200 rounded-sm overflow-hidden">
          {/* LEFT — info on dark pine */}
          <aside className="col-span-12 lg:col-span-5 bg-emerald-950 text-stone-100 p-8 lg:p-12 relative">
            <div className="hu-section-tag !text-amber-500 mb-6">Request a Quote</div>
            <h1 className="font-display font-bold text-4xl lg:text-5xl tracking-tight leading-[1.05] text-stone-100">
              Send us your plans —<br />we'll send back numbers.
            </h1>
            <p className="mt-6 text-stone-300 leading-relaxed">
              Most quotes are returned within 24–48 hours. The more detail you can share
              (drawings, dimensions, loading conditions), the faster we can turn it around.
            </p>

            <div className="mt-10 border-t border-emerald-800 pt-8 space-y-5">
              <div className="flex items-start gap-3">
                <Phone size={18} className="text-amber-500 mt-0.5" />
                <div>
                  <div className="text-xs uppercase tracking-widest text-amber-500/80">Phone</div>
                  <a href={COMPANY.phoneHref} className="block text-stone-100 hover:text-amber-400 mt-1 hu-ticker">
                    {COMPANY.phone}
                  </a>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Mail size={18} className="text-amber-500 mt-0.5" />
                <div>
                  <div className="text-xs uppercase tracking-widest text-amber-500/80">Email</div>
                  <a
                    href="mailto:dan@hurontruss.com"
                    data-testid="quote-email-dan"
                    className="block text-stone-100 hover:text-amber-400 mt-1"
                  >
                    dan@hurontruss.com
                  </a>
                  <a
                    href={`mailto:${COMPANY.email}`}
                    data-testid="quote-email-paul"
                    className="block text-stone-100 hover:text-amber-400 mt-1"
                  >
                    {COMPANY.email}
                  </a>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <MapPin size={18} className="text-amber-500 mt-0.5" />
                <div>
                  <div className="text-xs uppercase tracking-widest text-amber-500/80">Service Area</div>
                  <ul className="mt-1 space-y-1">
                    {COMPANY.serviceAreas.map((a) => (
                      <li key={a} className="text-stone-100 text-sm">{a}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>

            <div className="mt-12 border-t border-emerald-800 pt-8">
              <div className="text-xs uppercase tracking-widest text-amber-500/80 mb-3">Your contacts</div>
              <ul className="space-y-3">
                {COMPANY.team.map((m) => (
                  <li key={m.name}>
                    <div className="font-display font-bold text-stone-100">{m.name}</div>
                    <div className="text-xs text-stone-400">{m.role}</div>
                  </li>
                ))}
              </ul>
            </div>
          </aside>

          {/* RIGHT — form */}
          <div className="col-span-12 lg:col-span-7 bg-white p-8 lg:p-12">
            <form onSubmit={submit} className="space-y-6" data-testid="quote-form">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <Field label="Your Name *" testid="quote-name">
                  <input
                    type="text"
                    required
                    value={form.name}
                    onChange={(e) => update("name", e.target.value)}
                    data-testid="quote-input-name"
                    className="hu-input"
                  />
                </Field>
                <Field label="Company">
                  <input
                    type="text"
                    value={form.company}
                    onChange={(e) => update("company", e.target.value)}
                    data-testid="quote-input-company"
                    className="hu-input"
                  />
                </Field>
                <Field label="Email *">
                  <input
                    type="email"
                    required
                    value={form.email}
                    onChange={(e) => update("email", e.target.value)}
                    data-testid="quote-input-email"
                    className="hu-input"
                  />
                </Field>
                <Field label="Phone">
                  <input
                    type="tel"
                    value={form.phone}
                    onChange={(e) => update("phone", e.target.value)}
                    data-testid="quote-input-phone"
                    className="hu-input"
                  />
                </Field>
                <Field label="Project Location">
                  <input
                    type="text"
                    placeholder="Town, district"
                    value={form.project_location}
                    onChange={(e) => update("project_location", e.target.value)}
                    data-testid="quote-input-location"
                    className="hu-input"
                  />
                </Field>
                <Field label="Project Type">
                  <select
                    value={form.project_type}
                    onChange={(e) => update("project_type", e.target.value)}
                    data-testid="quote-input-project-type"
                    className="hu-input"
                  >
                    <option value="">Select…</option>
                    {PROJECT_TYPES.map((t) => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                </Field>
              </div>

              <Field label="Products of Interest">
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {PRODUCTS.map((p) => {
                    const active = form.products.includes(p.title);
                    return (
                      <button
                        type="button"
                        key={p.slug}
                        onClick={() => toggleProduct(p.title)}
                        data-testid={`quote-product-${p.slug}`}
                        className={`text-left text-xs sm:text-sm px-3 py-2.5 border rounded-sm transition-colors ${
                          active
                            ? "bg-emerald-950 text-stone-100 border-emerald-950"
                            : "bg-white text-neutral-700 border-stone-300 hover:border-neutral-900"
                        }`}
                      >
                        {p.title}
                      </button>
                    );
                  })}
                </div>
              </Field>

              <Field label="Timeline">
                <select
                  value={form.timeline}
                  onChange={(e) => update("timeline", e.target.value)}
                  data-testid="quote-input-timeline"
                  className="hu-input"
                >
                  <option value="">Select…</option>
                  {TIMELINES.map((t) => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
              </Field>

              <Field label="Project Details *">
                <textarea
                  required
                  rows={6}
                  value={form.details}
                  onChange={(e) => update("details", e.target.value)}
                  placeholder="Span, pitch, loading conditions, plans attached separately, anything we should know…"
                  data-testid="quote-input-details"
                  className="hu-input"
                />
              </Field>

              {status.type && (
                <div
                  data-testid={`quote-status-${status.type}`}
                  className={`flex items-start gap-3 p-4 border rounded-sm text-sm ${
                    status.type === "success"
                      ? "bg-emerald-50 border-emerald-200 text-emerald-900"
                      : "bg-red-50 border-red-200 text-red-900"
                  }`}
                >
                  {status.type === "success" ? (
                    <CheckCircle2 size={18} className="shrink-0 mt-0.5" />
                  ) : (
                    <AlertCircle size={18} className="shrink-0 mt-0.5" />
                  )}
                  <p>{status.msg}</p>
                </div>
              )}

              <div className="pt-2 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <p className="text-xs text-neutral-500 leading-relaxed max-w-md">
                  By submitting, you agree we may email you about your project. Your information
                  is never shared.
                </p>
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="quote-submit-button"
                  className="inline-flex items-center justify-center gap-2 bg-amber-700 hover:bg-amber-800 disabled:bg-stone-400 text-white text-sm font-medium px-6 py-3.5 rounded-sm transition-colors min-w-[180px]"
                >
                  {submitting ? (
                    <>
                      <Loader2 size={16} className="animate-spin" /> Sending…
                    </>
                  ) : (
                    <>Send Quote Request</>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* shared input styles */}
      <style>{`
        .hu-input {
          width: 100%;
          background: #fff;
          border: 1px solid #D6D3CD;
          border-radius: 2px;
          padding: 10px 12px;
          font-size: 14px;
          color: #1C2023;
          font-family: 'IBM Plex Sans', sans-serif;
          transition: border-color 0.15s, box-shadow 0.15s;
        }
        .hu-input:focus {
          outline: none;
          border-color: #C18341;
          box-shadow: 0 0 0 3px rgba(193, 131, 65, 0.18);
        }
        .hu-input::placeholder { color: #9CA39B; }
      `}</style>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-xs font-medium text-neutral-700 uppercase tracking-wider mb-2">
        {label}
      </span>
      {children}
    </label>
  );
}
